/**
 * Which i18n namespaces each route needs, so the router can preload them before
 * rendering the page.
 *
 * @module @app/constants
 */

import type { Namespace } from '@infra/i18n';
import { managementPaths } from '@modules/shell';

export const ROUTE_NAMESPACE_MAP: Array<{
  test: (path: string) => boolean;
  namespaces: Namespace[];
}> = [
  {
    test: (path) => path.startsWith(managementPaths.home('org')),
    namespaces: ['dashboard', 'relationships', 'skepticisms', 'stages', 'skills'],
  },
  {
    test: (path) => path.startsWith('/org/entitlements'),
    namespaces: ['organizations', 'entitlements'],
  },
  {
    test: (path) => path.startsWith('/org/analytics'),
    namespaces: ['organizations', 'analytics', 'relationships', 'skepticisms'],
  },
  {
    test: (path) => path.startsWith(managementPaths.home('admin')),
    namespaces: ['dashboard', 'relationships', 'skepticisms', 'stages', 'skills'],
  },
  { test: (path) => path.startsWith('/admin/users'), namespaces: ['users', 'organizations'] },
  {
    test: (path) => path.startsWith('/admin/scenarios') || path.startsWith('/org/scenarios'),
    namespaces: [
      'scenarios',
      'roleplay',
      'relationships',
      'skepticisms',
      'stages',
      'observable-signals',
    ],
  },
  {
    test: (path) =>
      path.startsWith('/admin/coaching') ||
      path.startsWith('/org/coaching') ||
      path.startsWith('/user/coaching'),
    // Same screen as /user/sessions/:id/report — same factor labels to resolve.
    namespaces: ['coaching', 'reports', 'roleplay', 'relationships', 'skepticisms', 'stages'],
  },
  {
    test: (path) => path.startsWith('/admin/organizations'),
    namespaces: ['organizations', 'entitlements', 'users'],
  },
  {
    test: (path) => path.startsWith('/org/organization'),
    namespaces: ['organizations', 'entitlements', 'users'],
  },
  {
    test: (path) => path.startsWith('/admin/entitlements'),
    namespaces: ['entitlements', 'organizations'],
  },
  {
    test: (path) =>
      path.startsWith('/admin/usage') ||
      path.startsWith('/org/usage') ||
      path.startsWith('/user/usage'),
    namespaces: ['roleplayUsage'],
  },
  {
    test: (path) => path.startsWith('/admin/analytics') || path.startsWith('/user/team'),
    namespaces: ['analytics', 'relationships', 'skepticisms', 'organizations'],
  },
  // Listed before the generic '/admin/settings' entry: the first match wins, so a
  // broader prefix placed first would swallow these.
  {
    test: (path) => path.startsWith('/admin/settings/stage-collection'),
    namespaces: ['stages', 'skills', 'observable-signals', 'settings'],
  },
  {
    test: (path) => path.startsWith('/admin/settings/stages'),
    namespaces: ['stages', 'skills', 'settings'],
  },
  {
    test: (path) => path.startsWith('/admin/settings/skills'),
    namespaces: ['skills', 'stages', 'observable-signals', 'settings'],
  },
  {
    test: (path) => path.startsWith('/admin/settings/observable-signals'),
    namespaces: ['observable-signals', 'skills', 'stages', 'settings'],
  },
  {
    test: (path) => path.startsWith('/admin/settings/system-configuration'),
    namespaces: ['system-configuration', 'settings'],
  },
  {
    test: (path) => path.startsWith('/admin/settings/ice-server'),
    namespaces: ['ice-server', 'settings'],
  },
  { test: (path) => path.startsWith('/admin/settings'), namespaces: ['settings'] },
  {
    test: (path) => path.startsWith('/admin/personas') || path.startsWith('/org/personas'),
    namespaces: ['personas', 'relationships', 'skepticisms'],
  },
  // `startsWith` also covers the legacy `-draft` URLs, which now only redirect.
  {
    test: (path) => path.startsWith(managementPaths.home('user')),
    namespaces: ['dashboard', 'personas', 'relationships', 'skepticisms'],
  },
  // The user detail pages read each other's namespaces (the persona page
  // labels its Start button from `scenarios`, the scenario page names personas)
  // and both raise the `roleplay` active-session dialog. A namespace missing here
  // still resolves — `t()` lazy-loads it — but renders its raw key until it lands.
  {
    test: (path) => path.startsWith('/user/personas'),
    namespaces: ['personas', 'scenarios', 'roleplay', 'relationships', 'skepticisms', 'stages'],
  },
  {
    test: (path) => path.startsWith('/user/scenarios'),
    namespaces: [
      'scenarios',
      'roleplay',
      'personas',
      'relationships',
      'skepticisms',
      'stages',
      'observable-signals',
      'booking',
    ],
  },
  { test: (path) => path.startsWith('/user/roleplay'), namespaces: ['roleplay', 'personas'] },
  {
    test: (path) => path.startsWith('/user/sessions'),
    // relationships/skepticisms: the report header names the factors the run
    // carried, and those labels live in their own namespaces.
    namespaces: ['reports', 'roleplay', 'relationships', 'skepticisms', 'stages'],
  },
  {
    test: (path) => path.startsWith('/user/analytics'),
    namespaces: ['analytics', 'reports', 'relationships', 'skepticisms', 'stages'],
  },
  {
    test: (path) => path.startsWith('/user/learning'),
    namespaces: ['learning', 'stages', 'skills'],
  },
  {
    test: (path) => path.startsWith('/user/profile') || path.startsWith('/org/profile'),
    namespaces: ['settings', 'users', 'organizations'],
  },
  {
    test: (path) => path.startsWith('/user/notifications') || path.startsWith('/org/notifications'),
    namespaces: ['booking'],
  },
  {
    test: (path) =>
      path.startsWith('/user/bookings') ||
      path.startsWith('/org/bookings') ||
      path.startsWith('/admin/bookings'),
    namespaces: ['booking', 'errors'],
  },
  {
    test: (path) => path.startsWith('/user/change-password') || path.startsWith('/org/change-password'),
    namespaces: ['settings', 'users'],
  },
  { test: (path) => path.startsWith('/admin/change-password'), namespaces: ['settings', 'users'] },
];
